import { useCallback, useMemo } from 'react';

import { faCode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { MultiSelect } from '@repo/videa-components/src/multi-select';
import { Button } from '@repo/videa-components/src/ui/button';
import { Textarea } from '@repo/videa-components/src/ui/textarea';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash';

import type { PreferencesResponse } from '../../../../@types/preferences';
import type { TFlattenedTreatment } from '../../../../@types/rcm';
import AiIndicator from '../../../patientList/filtersSidebar/AiIndicator';
import { FormInput, TEETH_IMAGE_SETS_VALUES } from '../form';
import type { InsuranceView } from '../insuranceExport/constants';
import {
  clinicalNotesAtom,
  dateOfVisitAtom,
  imageSetsAtom,
  patientDOBAtom,
  patientNameAtom,
  selectedTreatmentsAtom,
} from '../state';

export const PatientInfo = ({
  preferences,
  defaultTreatments,
  treatmentsToUse,
  isSrp,
  onSelectTreatments,
  onSetInsPage,
}: {
  preferences: PreferencesResponse;
  defaultTreatments: TFlattenedTreatment[];
  treatmentsToUse: TFlattenedTreatment[];
  estimatedVisitId: number;
  unifiedAppointmentId?: number;
  isSrp: boolean;
  onSelectTreatments: (txIds: string[]) => void;
  onSetInsPage: (value: InsuranceView) => void;
}) => {
  const selectedTreatments = useAtomValue(selectedTreatmentsAtom);
  const imageSets = useAtomValue(imageSetsAtom);
  const [patientName, setPatientName] = useAtom(patientNameAtom);
  const [patientDOB, setPatientDOB] = useAtom(patientDOBAtom);
  const [dateOfVisit, setDateOfVisit] = useAtom(dateOfVisitAtom);
  const [clinicalNotes, setClinicalNotes] = useAtom(clinicalNotesAtom);

  const treatmentList = defaultTreatments.map((t) => ({
    value: t.txId.toString(),
    label: `${t.txCode} - ${t.quadrant ?? t.toothId}`,
    disabled:
      treatmentsToUse
        .map((tx) => tx.txId.toString())
        .includes(t.txId.toString()) && treatmentsToUse.length <= 1,
  }));

  const debouncedOnChange = useMemo(
    () =>
      debounce((value: any) => {
        onSelectTreatments(value);
      }, 100),
    [onSelectTreatments],
  );

  const handleChange = useCallback(
    (value: any) => {
      debouncedOnChange(value);
    },
    [debouncedOnChange],
  );

  return (
    <>
      <div className="vai-text-lg vai-font-medium">
        <Trans>Treatment Information</Trans>
      </div>
      <div className="vai-grid vai-grid-flow-row vai-gap-5 vai-mt-4">
        <div className="vai-flex vai-items-center vai-gap-2 vai-w-full">
          {selectedTreatments && selectedTreatments.length > 0 && (
            <>
              <div className="vai-w-32 vai-text-sm vai-font-medium">
                <Trans>Select Treatments To Include</Trans>
              </div>
              <div className="vai-flex-1 vai-border vai-rounded-md">
                <MultiSelect
                  options={treatmentList}
                  onValueChange={handleChange}
                  defaultValue={selectedTreatments}
                  placeholder="Select options"
                  variant="inverted"
                  animation={0}
                  maxCount={5}
                  className="vai-bg-white hover:vai-bg-white/80"
                  disabled={imageSets === TEETH_IMAGE_SETS_VALUES.All}
                  disableClearAll
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="vai-text-lg vai-font-medium vai-mt-4">
        <Trans>Patient Information</Trans>
      </div>
      <div className="vai-grid vai-grid-flow-row vai-gap-5 vai-mt-4">
        <FormInput
          onChange={(value) => setPatientName(value)}
          value={patientName}
          label="Patient Name"
          placeholder="Patient Name"
        />

        <FormInput
          onChange={(value) => setPatientDOB(value)}
          value={patientDOB}
          label="Patient DOB"
          placeholder="Patient DOB"
        />

        <FormInput
          onChange={(value) => setDateOfVisit(value)}
          value={dateOfVisit}
          label="Date of Visit"
          placeholder="Date of Visit"
        />

        <div className="vai-flex vai-items-center vai-gap-2 vai-w-full">
          <div className="vai-w-32 vai-text-sm vai-font-medium">
            <AiIndicator text="Narrative" />
            {preferences?.narrativeDevToolEnabled && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onSetInsPage('narrative-dev-tool')}
              >
                <FontAwesomeIcon icon={faCode} />
                AI Config
              </Button>
            )}
          </div>
        </div>
        {isSrp && (
          <div className="vai-flex vai-items-center vai-gap-2 vai-w-full">
            <div className="vai-w-32 vai-text-sm vai-font-medium">
              <Trans>Clinical Notes</Trans>
            </div>
            <div className="vai-flex-1">
              <div className="vai-flex-1">
                <Textarea
                  placeholder="Clinical Notes"
                  className="vai-min-h-[250px]"
                  value={clinicalNotes}
                  onChange={(e) => setClinicalNotes(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
